/* eslint-disable no-console */
import { IMapConfigEntry, IMapConfigEntryDomination } from '@nova/types';

import { MapRepository } from './maps.repository';

export class MapService {
  private _mapRepo: MapRepository;

  public constructor(mapRepo?: MapRepository) {
    this._mapRepo = mapRepo || new MapRepository();
  }

  public async init(gameAPIEndpoint: string): Promise<void> {
    await this._mapRepo.init(gameAPIEndpoint);
  }

  public getAll(debugMode?: boolean): (IMapConfigEntry | IMapConfigEntryDomination)[] {
    let mapsOr = this._mapRepo.getOrMapsCopied();
    let mapsDom = this._mapRepo.getDominationMapsCopied();

    // filter out debug maps
    if (!debugMode) {
      mapsOr = mapsOr.filter((c) => !c.debugOnly);
      mapsDom = mapsDom.filter((c) => !c.debugOnly);
    }

    return [...mapsOr, ...mapsDom];
  }

  /**
   *
   * @param mapId
   * @returns IMapConfigEntry | IMapConfigEntryDomination
   */
  public findById(mapId: string): IMapConfigEntry | IMapConfigEntryDomination {
    const map = this._mapRepo.findMapByIdOr(mapId) || this._mapRepo.findByIdDomination(mapId);
    if (!map) { throw new Error(`Could not find map based on mapId <${mapId}>`); }
    return map;
  }

  public getPlayerCap(map: IMapConfigEntry): number {
    return map.teamSize;
  }

  public getTotalPlayerCap(): number {
    const maps = this.getAll();
    return this.getMaxPlayerCap(maps);
  }

  public getMultiPlayerMaps(): IMapConfigEntry[] {
    const mapsDom = this._mapRepo.getDominationMapsCopied();
    // Filters out just the tutorial map, so far.
    return mapsDom.filter((map) => map.teamCount > 1 || map.teamSize > 0);
  }

  private getMaxPlayerCap(maps: IMapConfigEntry[]): number {
    let runningPlayerCap = 1;
    for (const map of maps) {
      const mapPlayerCap = this.getPlayerCap(map);
      if (mapPlayerCap > runningPlayerCap) {
        runningPlayerCap = mapPlayerCap;
      }
    }
    return runningPlayerCap;
  }

  public getMultiPlayerPlayerCap(): number {
    const maps = this.getMultiPlayerMaps();
    return this.getMaxPlayerCap(maps);
  }

  /**
   *
   * @param playerCount
   * @returns the ids of the maps that can host the given number of players based on their team size.
   */
  public getMapIdsByPlayerCount(playerCount: number): string[] {
    const allMaps = this.getAll();
    const filteredMaps = allMaps.filter((map) => map.teamSize >= playerCount);
    return filteredMaps.map((map) => map.id);
  }

  public isPlayerCountValid(mapId: string, playerCount: number): boolean {
    const map = this.findById(mapId);
    if (!map) {
      throw new Error(`MapService::isPlayerCountValid: Map ${mapId} not found`);
    }

    const playerCountOverTeamSize = playerCount > map.teamSize;
    if (playerCountOverTeamSize) {
      console.error(`MapService::isPlayerCountValid: Player count <${playerCount}> over team size <${map.teamSize}> for map <${mapId}>.`);
      return false;
    }

    return true;
  }
}
